const validateFunc = (_event, _form, input) =>
  Boolean(input && input.val() && input.val().length > 0);

const toggleDisplay = (event) => {
  if (event?.target) {
    const scheduleChangeQuestion = document.getElementById('cx_webchat_form_schedule_change_row');

    if (event.target.value === 'No') {
      scheduleChangeQuestion.style.display = 'table-row';
    } else {
      scheduleChangeQuestion.style.display = 'none';
    }
  }
};

const setCssForRTL = async () => {
  try {
    await import('@eti/styles/genesys/light-rtl.css');

    return {
      'light-rtl': 'cx-theme-light-rtl',
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(error);

    return {};
  }
};

const createChatWidgetConfiguration = async (
  webChatServiceUrl,
  genesysWidgetsUrl,
  deploymentKey,
  orgGuid,
  siteName,
  texts,
  siteLanguageCode,
  languageCodesWithoutNativeSupport,
  shouldShowQuestions,
  userEmail,
  orderNumber,
  isRebookingPage,
  extraInfo,
) => {
  const inputs = [
    {
      id: 'cx_webchat_form_firstname',
      name: 'firstname',
      maxlength: '100',
      placeholder: texts.required,
      label: '@i18n:webchat.ChatFormFirstName',
      validateWhileTyping: false,
      validate: validateFunc,
    },
    {
      id: 'cx_webchat_form_lastname',
      name: 'lastname',
      maxlength: '100',
      placeholder: texts.optional,
      label: '@i18n:webchat.ChatFormLastName',
    },
    {
      id: 'cx_webchat_form_email',
      name: 'email',
      maxlength: '100',
      placeholder: texts.required,
      label: '@i18n:webchat.ChatFormEmail',
      validateWhileTyping: false,
      validate: validateFunc,
      value: userEmail,
    },
    {
      id: 'cx_webchat_form_ordernumber',
      name: 'customField1',
      maxlength: '100',
      placeholder: texts.optional,
      label: texts.orderNumber,
      value: orderNumber,
    },
  ];

  if (shouldShowQuestions && !isRebookingPage) {
    inputs.push(
      {
        id: 'cx_webchat_form_unexpected_events',
        name: 'unexpectedEvents',
        type: 'select',
        label: `<span style='display: inline-block; width: 150px; white-space: normal; word-wrap: break-word;'>${texts.unexpectedEventsQuestion}</span>`,
        options: [
          {
            text: texts.select,
            disabled: true,
            hidden: true,
            selected: true,
            value: '',
          },
          {
            text: texts.answerYes,
            value: 'Yes',
          },
          {
            text: texts.answerNo,
            value: 'No',
          },
        ],
        onchange: toggleDisplay,
        validate: validateFunc,
      },
      {
        id: 'cx_webchat_form_schedule_change',
        name: 'scheduleChange',
        type: 'select',
        label: `<span style='display: inline-block; width: 150px; white-space: normal; word-wrap: break-word;'>${texts.scheduleChangeQuestion}</span>`,
        options: [
          {
            text: texts.select,
            disabled: true,
            hidden: true,
            selected: true,
            value: '',
          },
          {
            text: texts.answerYes,
            value: 'Yes',
          },
          {
            text: texts.answerNo,
            value: 'No',
          },
        ],
        wrapper:
          '<tr id="cx_webchat_form_schedule_change_row" style="display: none;"><th>{label}</th><td>{input}</td></tr>',
      },
    );
  }

  // eslint-disable-next-line no-underscore-dangle
  window._genesys = {
    widgets: {
      main: {
        lang: siteLanguageCode,
        i18n: languageCodesWithoutNativeSupport?.includes(siteLanguageCode)
          ? await import(`@eti/assets/texts/genesys/${siteLanguageCode}.json`).catch((error) =>
              // eslint-disable-next-line no-console
              console.warn(error),
            )
          : `${genesysWidgetsUrl}/i18n/widgets-${siteLanguageCode}.i18n.json`,
        themes: window.spa_variables.isRTL ? await setCssForRTL() : {},
        theme: window.spa_variables.isRTL ? 'light-rtl' : 'light',
      },
      webchat: {
        transport: {
          type: 'purecloud-v2-sockets',
          dataURL: webChatServiceUrl,
          deploymentKey,
          orgGuid,
        },
        userData: {
          customField1Label: 'Order Number',
          siteName,
          language: siteLanguageCode,
          selfService: isRebookingPage ? 'rebooking' : '',
          ...extraInfo,
        },
        form: {
          wrapper: '<table></table>',
          inputs,
        },
        confirmFormCloseEnabled: false,
      },
    },
  };
};

const createGenesysWidgetScriptTag = (genesysWidgetsUrl) => {
  const genesysWidgetScript = document.createElement('script');
  genesysWidgetScript.src = `${genesysWidgetsUrl}/cxbus.min.js`;
  genesysWidgetScript.addEventListener('load', () => {
    // eslint-disable-next-line no-undef
    CXBus.configure({
      debug: false,
      pluginsPath: `${genesysWidgetsUrl}/plugins/`,
    });

    // eslint-disable-next-line no-undef
    CXBus.loadPlugin('widgets-core');
  });

  document.body.appendChild(genesysWidgetScript);
};

export const initChatWidget = (
  webChatServiceUrl,
  genesysWidgetsUrl,
  deploymentKey,
  orgGuid,
  siteName,
  texts,
  siteLanguageCode,
  languageCodesWithoutNativeSupport,
  shouldShowQuestions,
  userEmail,
  orderNumber,
  isRebookingPage,
  extraInfo,
) => {
  createChatWidgetConfiguration(
    webChatServiceUrl,
    genesysWidgetsUrl,
    deploymentKey,
    orgGuid,
    siteName,
    texts,
    siteLanguageCode,
    languageCodesWithoutNativeSupport,
    shouldShowQuestions,
    userEmail,
    orderNumber,
    isRebookingPage,
    extraInfo,
  )
    .then(() => createGenesysWidgetScriptTag(genesysWidgetsUrl))
    .catch(console.error); // eslint-disable-line no-console
};

export const openGenesysChat = () => {
  // eslint-disable-next-line no-undef
  if (CXBus) {
    // eslint-disable-next-line no-undef
    CXBus.command('WebChat.open');
  }
};

export const closeGenesysChat = () => {
  // eslint-disable-next-line no-undef
  if (CXBus) {
    // eslint-disable-next-line no-undef
    CXBus.command('WebChat.close');
  }
};
